import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, apiError, MODALITY_LABEL } from "@/components/admin/api";
import StatusBadge from "@/components/admin/StatusBadge";
import BookingCard from "@/components/admin/BookingCard";
import SlotPicker from "@/components/admin/SlotPicker";
import { formatPrice } from "@/data/site";

const PackagesView = ({ focusCode, onClearFocus }) => {
  const [packages, setPackages] = useState(null);
  const [detail, setDetail] = useState(null);
  const [acting, setActing] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");

  const load = useCallback(() => {
    adminApi
      .get("/admin/journey/packages")
      .then((res) => setPackages(res.data))
      .catch((e) => toast.error(apiError(e, "Não foi possível carregar os pacotes.")));
  }, []);

  useEffect(load, [load]);

  const openDetail = useCallback((code) => {
    adminApi
      .get(`/admin/journey/packages/${code}`)
      .then((res) => setDetail(res.data))
      .catch((e) => toast.error(apiError(e, "Pacote não encontrado.")));
  }, []);

  useEffect(() => {
    if (focusCode) {
      openDetail(focusCode);
      onClearFocus?.();
    }
  }, [focusCode, openDetail, onClearFocus]);

  const reload = () => {
    load();
    if (detail) openDetail(detail.code);
  };

  const onAction = async (protocol, payload) => {
    if (payload.action === "cancelar" && !window.confirm("Cancelar esta sessão?")) return;
    setActing(true);
    try {
      await adminApi.patch(`/admin/journey/bookings/${protocol}`, payload);
      toast.success("Sessão atualizada.");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActing(false);
    }
  };

  const addSession = async () => {
    setActing(true);
    try {
      await adminApi.post(`/admin/journey/packages/${detail.code}/sessions`, { date: newDate, time: newTime });
      toast.success("Sessão agendada.");
      setNewDate("");
      setNewTime("");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActing(false);
    }
  };

  const cancelPackage = async () => {
    if (!window.confirm("Cancelar o pacote? As sessões futuras serão canceladas.")) return;
    setActing(true);
    try {
      await adminApi.post(`/admin/journey/packages/${detail.code}/cancel`);
      toast.success("Pacote cancelado.");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActing(false);
    }
  };

  if (!packages) {
    return <div className="h-64 animate-pulse rounded-3xl border border-raiz-900/10 bg-cream" data-testid="pacotes-loading" />;
  }

  if (detail) {
    const canSchedule = detail.status === "ativo" && detail.utilizadas + detail.ativas < detail.contratadas;
    return (
      <div data-testid="pacote-detail">
        <button type="button" data-testid="pacote-voltar" onClick={() => setDetail(null)} className="text-sm text-raiz-800 underline underline-offset-4">
          ← Voltar para a lista de pacotes
        </button>
        <div className="mt-5 rounded-3xl border border-raiz-900/10 bg-paper p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-serif text-2xl font-light text-ink" data-testid="pacote-code">{detail.code}</h3>
                <StatusBadge status={detail.status} testId="pacote-status" />
              </div>
              <p className="mt-2 text-sm text-ink/65">
                {detail.patient.name} — {detail.service_title} • {detail.plan_name} • {MODALITY_LABEL[detail.modality]}
                {detail.price != null ? ` • ${formatPrice(detail.price)}` : ""}
              </p>
              <p className="mt-1 text-xs text-ink/50">
                WhatsApp {detail.patient.whatsapp} • {detail.patient.email}
              </p>
            </div>
            <button
              type="button"
              data-testid="pacote-copiar-codigo"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(detail.code);
                  toast.success("Código copiado para enviar ao paciente.");
                } catch {
                  toast.info(`Código do pacote: ${detail.code}`, { duration: 8000 });
                }
              }}
              className="rounded-full border border-raiz-800/25 px-4 py-2 text-xs font-medium text-raiz-800 hover:border-raiz-800"
            >
              Copiar código
            </button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3" data-testid="pacote-contadores">
            <div className="rounded-2xl border border-raiz-900/10 bg-cream p-4 text-center">
              <p className="font-serif text-3xl text-raiz-900" data-testid="pacote-contratadas">{detail.contratadas}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wide sm:tracking-widest text-ink/50">Contratadas</p>
            </div>
            <div className="rounded-2xl border border-raiz-900/10 bg-cream p-4 text-center">
              <p className="font-serif text-3xl text-raiz-900" data-testid="pacote-utilizadas">{detail.utilizadas}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wide sm:tracking-widest text-ink/50">Utilizadas</p>
            </div>
            <div className="rounded-2xl border border-gold-400/50 bg-gold-100/40 p-4 text-center">
              <p className="font-serif text-3xl text-gold-700" data-testid="pacote-restantes">{detail.restantes}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wide sm:tracking-widest text-ink/50">Restantes</p>
            </div>
          </div>

          <h4 className="mt-8 font-serif text-xl text-ink">Sessões</h4>
          <div className="mt-4 space-y-3" data-testid="pacote-sessoes">
            {detail.sessions.map((s) => (
              <BookingCard key={s.protocol} b={s} onAction={onAction} acting={acting} />
            ))}
          </div>

          {detail.status === "ativo" && (
            <div className="mt-8 rounded-2xl border border-raiz-900/10 bg-cream p-5" data-testid="pacote-agendar-form">
              <p className="text-sm font-medium text-ink">Agendar próxima sessão</p>
              {!canSchedule && (
                <p className="mt-2 text-xs text-ink/55">Todas as sessões já estão agendadas ou utilizadas.</p>
              )}
              {canSchedule && (
                <div className="mt-3">
                  <SlotPicker date={newDate} time={newTime} onDate={setNewDate} onTime={setNewTime} testPrefix="pacote" />
                  <button
                    type="button"
                    data-testid="pacote-agendar-button"
                    onClick={addSession}
                    disabled={acting || !newDate || !newTime}
                    className="mt-4 rounded-full bg-raiz-800 px-5 py-2.5 text-xs font-medium text-paper hover:bg-raiz-700 disabled:opacity-50"
                  >
                    Agendar sessão
                  </button>
                </div>
              )}
            </div>
          )}

          {detail.status === "ativo" && (
            <button type="button" data-testid="pacote-cancelar-button" onClick={cancelPackage} disabled={acting} className="mt-6 rounded-full border border-red-500/40 px-5 py-2.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
              Cancelar pacote
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pacotes-view">
      <p className="max-w-xl text-sm leading-relaxed text-ink/60">
        Pacotes nascem quando você confirma o pagamento de um agendamento de pacote (4 ou 8 sessões). O código pode ser enviado ao paciente para ele agendar as próximas sessões pelo site.
      </p>
      {packages.length === 0 ? (
        <p className="mt-8 rounded-2xl border border-raiz-900/10 bg-cream p-6 text-sm text-ink/60" data-testid="pacotes-vazio">
          Nenhum pacote ainda. Eles aparecem aqui quando um pacote for confirmado.
        </p>
      ) : (
        <div className="mt-6 space-y-4" data-testid="pacotes-lista">
          {packages.map((p) => (
            <button
              key={p.code}
              type="button"
              data-testid={`pacote-card-${p.code}`}
              onClick={() => openDetail(p.code)}
              className="w-full rounded-2xl border border-raiz-900/10 bg-paper p-5 text-left transition-colors hover:border-gold-400/60"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-serif text-lg text-ink">
                    {p.code} — {p.patient.name}
                  </p>
                  <p className="mt-1 text-sm text-ink/60">
                    {p.service_title} • {p.plan_name}
                  </p>
                  <p className="mt-2 text-xs text-ink/55">
                    Contratadas {p.contratadas} • Utilizadas {p.utilizadas} • Restantes {p.restantes}
                  </p>
                </div>
                <StatusBadge status={p.status} testId={`pacote-status-${p.code}`} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PackagesView;
